import { useEffect, useState } from 'react';
import { FundEntity, FundWithDetailsEntity, TokenWithAmountForDatum } from '../lib/MayzSmartDB';
import { useWalletStore } from '../store/global';
import { INVESTUNIT_MULTIPLIER } from '../utils/constants';

interface FundDepositWithdrawCalculationsProps {
    fund?: FundWithDetailsEntity;
    serverTime?: number;
    isServerTimeLoaded: boolean;
}

export const useFundDepositWithdrawCalculations = (props: FundDepositWithdrawCalculationsProps) => {
    //--------------------------------------
    const { fund, serverTime, isServerTimeLoaded } = props;
    //--------------------------------------
    const walletStore = useWalletStore();
    //--------------------------------------
    const [available_FT_in_Wallet_Based_On_Tokens, setAvailable_FT_in_Wallet_Based_On_Tokens] = useState(0n);
    const [available_FT_in_Wallet, setAvailable_FT_in_Wallet] = useState(0n);
    const [amount_Tokens_Available_In_Wallet, setAmount_Tokens_Available_In_Wallet] = useState<TokenWithAmountForDatum[]>([]);
    const [amount_Tokens_Available_In_Wallet_Based_On_FT, setAmount_Tokens_Available_In_Wallet_Based_On_FT] = useState<TokenWithAmountForDatum[]>([]);
    const [isMaxAmountLoaded, setIsMaxAmountLoaded] = useState(false);
    //--------------------------------------
    useEffect(() => {
        //-----------
        if (walletStore.isConnected === false) {
            setIsMaxAmountLoaded(true);
            return;
        }
        if (fund !== undefined && walletStore.isWalletDataLoaded === true && isServerTimeLoaded === true && serverTime !== undefined) {
            //--------------------------------------
            setIsMaxAmountLoaded(false);
            //--------------------------------------
            const now = serverTime;
            //--------------------------------------
            const deadline = fund.fdDeadline;
            const commissions_Table_Numerator_1e6 = fund.fdCommissions_Table_Numerator_1e6;
            //---------------------
            const available_FT_in_Wallet = walletStore.getTotalOfUnit(fund.getNET_id_CS() + fund.fdFundFT_TN_Hex);
            setAvailable_FT_in_Wallet(available_FT_in_Wallet);
            //-----------
            const { withdrawPlusCommissionsGetBack } = FundEntity.calculateWithdrawCommissions(commissions_Table_Numerator_1e6, deadline, BigInt(now), available_FT_in_Wallet);
            //-----------
            const investUnitTokens = fund.investUnit!.investUnitWithADAPrices.iuValuesWithDetailsAndAmount;
            //-----------
            const amount_Tokens_Available_In_Wallet_Based_On_FT: TokenWithAmountForDatum[] = investUnitTokens.map((tokenWithAmount) => {
                const cs = tokenWithAmount.CS;
                const tn = tokenWithAmount.TN_Hex;
                const requiredAmountDecimals = Number(tokenWithAmount.amount) / INVESTUNIT_MULTIPLIER; // invest unit is mulplied by 100
                const amountDecimals = Number(withdrawPlusCommissionsGetBack) * requiredAmountDecimals;
                return [cs, tn, BigInt(Math.floor(amountDecimals))];
            });
            setAmount_Tokens_Available_In_Wallet_Based_On_FT(amount_Tokens_Available_In_Wallet_Based_On_FT);
            //-----------
            const tokensWithWalletAmount: TokenWithAmountForDatum[] = investUnitTokens.map((tokenWithAmount) => {
                const cs = tokenWithAmount.CS;
                const tn = tokenWithAmount.TN_Hex;
                const walletAmount = walletStore.getTotalOfUnit(cs + tn);
                return [cs, tn, walletAmount];
            });
            //-----------
            setAmount_Tokens_Available_In_Wallet(tokensWithWalletAmount);
            //-----------
            let minInvestUnitsDecimals: number | undefined = undefined;
            //-----------
            investUnitTokens.forEach((investUnitToken) => {
                //-----------
                const walletAmountEntry = tokensWithWalletAmount.find(([walletCs, walletTn]) => walletCs === investUnitToken.CS && walletTn === investUnitToken.TN_Hex);
                const walletAmount = walletAmountEntry ? walletAmountEntry[2] : BigInt(0);
                const requiredAmountDecimals = Number(investUnitToken.amount) / INVESTUNIT_MULTIPLIER; // invest unit is mulplied by 100
                //-----------
                if (Number(walletAmount) >= requiredAmountDecimals && requiredAmountDecimals > 0) {
                    const possibleUnitsDecimals = Number(walletAmount) / requiredAmountDecimals;
                    minInvestUnitsDecimals =
                        minInvestUnitsDecimals === undefined || possibleUnitsDecimals < minInvestUnitsDecimals ? possibleUnitsDecimals : minInvestUnitsDecimals;
                } else {
                    minInvestUnitsDecimals = 0;
                    return;
                }
            });
            //-----------
            const minInvestUnits = BigInt(Math.floor(minInvestUnitsDecimals ?? 0));
            // no necesito decimales, solo quiero el menor valor entero de FT que se pueden conseguir con los tokens que tengo
            //---------------------
            const { userFT } = FundEntity.calculateDepositCommissions(commissions_Table_Numerator_1e6, deadline, BigInt(now), minInvestUnits);
            //---------------------
            // basado en los tokens que tengo, busco cual es el que menos FT puede comprar y en base a ese seteo el maximo de FT que puedo comprar
            setAvailable_FT_in_Wallet_Based_On_Tokens(userFT);
            //-----------
            setIsMaxAmountLoaded(true);
            //-----------
        }
    }, [fund, walletStore.isWalletDataLoaded, walletStore.isConnected, isServerTimeLoaded, serverTime]);

    //--------------------------------------
    return {
        available_FT_in_Wallet_Based_On_Tokens,
        available_FT_in_Wallet,
        amount_Tokens_Available_In_Wallet,
        amount_Tokens_Available_In_Wallet_Based_On_FT,
        isMaxAmountLoaded,
    };
    //--------------------------------------
};
